import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/EditOutlined'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import FactoryIcon from '@mui/icons-material/Factory'
import InventoryIcon from '@mui/icons-material/Inventory2'
import type { Item } from '@iobiz/shared'
import { format, money, type CurrencyCode } from '@iobiz/shared'
import { useItems, useSupplier } from '../data/hooks.js'
import { removeSupplier } from '../data/repositories/catalog.write.js'
import { SupplierFormDialog } from './SupplierListPage.js'
import { VignetteFiche } from '../ui/VignetteFiche.js'
import { ConfirmDialog } from '../ui/ConfirmDialog.js'
import { DataTable, EmptyState, type Column } from '../ui/DataTable.js'
import { useSessionStore } from '../stores/sessionStore.js'

/**
 * Fiche fournisseur — troisième reprise de la charpente 360° (entête, onglets,
 * panneau latéral) après le client et l'article.
 *
 * L'onglet « Articles fournis » est ce qui justifie une fiche plutôt qu'un
 * simple formulaire : la question qu'on se pose devant un fournisseur, c'est
 * « qu'est-ce qu'il me fournit ? », pas « quel est son code postal ? ».
 */

function Field({ label, value }: { label: string; value: string | number | null }): JSX.Element {
  return (
    <Box sx={{ display: 'flex', py: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', width: 180, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2">
        {value === null || value === '' ? '—' : String(value)}
      </Typography>
    </Box>
  )
}

const TERMS: Record<number, string> = {
  0: 'Comptant',
  15: '15 jours',
  30: '30 jours',
  45: '45 jours',
  60: '60 jours',
}

export function SupplierDetailPage(): JSX.Element {
  const { slug, id } = useParams<{ slug: string; id: string }>()
  const navigate = useNavigate()
  const store = useSessionStore((s) => (slug ? s.storeBySlug(slug) : undefined))

  const supplier = useSupplier(store?.storeId, id)
  const page = useItems(store?.storeId, { supplierId: id, limit: 200 })
  const items = page?.data ?? []
  const itemsLoading = page === undefined
  const loading = supplier === undefined
  const [tab, setTab] = useState(0)
  const [editing, setEditing] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [confirming, setConfirming] = useState(false)

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 300 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (!supplier) {
    return (
      <Alert severity="error" action={<Button onClick={() => navigate(-1)}>Retour</Button>}>
        Fournisseur introuvable dans les données de cet appareil.
      </Alert>
    )
  }

  const currency = (store?.currencyCode ?? 'XAF') as CurrencyCode
  const price = (amount: number): string => format(money(amount, currency), 'fr-FR')

  const columns: Column<Item>[] = [
    {
      key: 'name',
      label: 'Article',
      render: (i) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {i.name}
          </Typography>
          {i.sku && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {i.sku}
            </Typography>
          )}
        </Box>
      ),
    },
    { key: 'category', label: 'Catégorie', render: (i) => i.categoryName ?? '—' },
    {
      key: 'purchase',
      label: "Prix d'achat",
      align: 'right',
      numeric: true,
      render: (i) => price(i.purchasePrice),
    },
    {
      key: 'sale',
      label: 'Prix de vente',
      align: 'right',
      numeric: true,
      render: (i) => price(i.salePrice),
    },
  ]

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 4 }}>
        <Link
          component="button"
          variant="body2"
          underline="hover"
          onClick={() => navigate(`/s/${slug}/catalogue/fournisseurs`)}
        >
          Fournisseurs
        </Link>
        <Typography variant="body2" color="text.primary">
          {supplier.companyName}
        </Typography>
      </Breadcrumbs>

      <Paper sx={{ p: 6, borderRadius: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Le fournisseur n'a pas encore de visuel : la vignette reste
              muette, mais occupe la même place et la même taille qu'ailleurs.
              Le jour où un logo existera, il s'affichera ici sans rien changer
              d'autre. */}
          <VignetteFiche
            images={[]}
            repli={<FactoryIcon sx={{ fontSize: 24, color: 'text.disabled' }} />}
          />

          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="h1">{supplier.companyName}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              {[supplier.code, supplier.contactName, supplier.city].filter(Boolean).join(' · ') ||
                'Aucun contact renseigné'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                icon={<InventoryIcon sx={{ fontSize: 16 }} />}
                label={`${supplier.itemCount} article${supplier.itemCount > 1 ? 's' : ''}`}
              />
              <Chip
                size="small"
                variant="outlined"
                label={TERMS[supplier.paymentTermsDays] ?? `${supplier.paymentTermsDays} jours`}
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" startIcon={<EditIcon />} onClick={() => setEditing(true)}>
              Modifier
            </Button>
            <IconButton
              aria-label="Autres actions"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null)
                  setConfirming(true)
                }}
                sx={{ color: 'error.main' }}
              >
                <DeleteIcon sx={{ fontSize: 18, mr: 2 }} />
                Supprimer
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Paper>

      <Tabs value={tab} onChange={(_, v: number) => setTab(v)} sx={{ mb: 4 }}>
        <Tab label="Informations" />
        <Tab label={`Articles fournis (${supplier.itemCount})`} />
        <Tab label="Commandes" disabled />
      </Tabs>

      {tab === 0 && (
        <Box
          sx={{
            display: 'grid',
            gap: 4,
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
            alignItems: 'start',
          }}
        >
          <Paper sx={{ p: 6, borderRadius: 3 }}>
            <Typography variant="h3" sx={{ mb: 4 }}>
              Contact
            </Typography>
            <Field label="Interlocuteur" value={supplier.contactName} />
            <Field label="Téléphone" value={supplier.phone} />
            <Field label="Email" value={supplier.email} />
            <Field label="Site web" value={supplier.website} />

            <Typography variant="h3" sx={{ mb: 4, mt: 6 }}>
              Adresse
            </Typography>
            <Field label="Adresse" value={supplier.addressLine1} />
            <Field label="Code postal" value={supplier.postalCode} />
            <Field label="Ville" value={supplier.city} />
            <Field label="Pays" value={supplier.countryCode} />

            <Typography variant="h3" sx={{ mb: 4, mt: 6 }}>
              Notes
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {supplier.notes?.trim() ? supplier.notes : '—'}
            </Typography>
          </Paper>

          <Paper sx={{ p: 6, borderRadius: 3 }}>
            <Typography variant="h3" sx={{ mb: 4 }}>
              Commercial
            </Typography>
            <Field label="Référence" value={supplier.code} />
            <Field label="N° fiscal" value={supplier.taxId} />
            <Field
              label="Conditions de paiement"
              value={TERMS[supplier.paymentTermsDays] ?? `${supplier.paymentTermsDays} jours`}
            />

            <Typography variant="h3" sx={{ mb: 4, mt: 6 }}>
              Suivi
            </Typography>
            <Field
              label="Créé le"
              value={new Date(supplier.createdAt).toLocaleDateString('fr-FR')}
            />
            <Field label="Version" value={supplier.version} />
          </Paper>
        </Box>
      )}

      {tab === 1 && (
        <DataTable
          columns={columns}
          rows={items}
          rowKey={(i) => i.id}
          loading={itemsLoading}
          total={items.length}
          limit={200}
          offset={0}
          onPageChange={() => undefined}
          onRowClick={(i) => navigate(`/s/${slug}/catalogue/${i.id}`)}
          emptyState={
            <EmptyState
              icon={<InventoryIcon sx={{ fontSize: 48 }} />}
              title="Aucun article"
              description="Aucun article du catalogue n’est rattaché à ce fournisseur."
            />
          }
        />
      )}

      {store && (
        <SupplierFormDialog
          open={editing}
          storeId={store.storeId}
          supplier={supplier}
          onClose={() => setEditing(false)}
          onSaved={() => undefined}
        />
      )}

      <ConfirmDialog
        open={confirming}
        title="Supprimer ce fournisseur ?"
        message={
          supplier.itemCount > 0
            ? `${supplier.companyName} fournit ${supplier.itemCount} article` +
              `${supplier.itemCount > 1 ? 's' : ''}. ` +
              `${supplier.itemCount > 1 ? 'Ils resteront' : 'Il restera'} au catalogue, ` +
              'sans fournisseur rattaché.'
            : `${supplier.companyName} n’apparaîtra plus dans les listes ni dans la recherche.`
        }
        onConfirm={async () => {
          await removeSupplier(store!.storeId, supplier)
          navigate(`/s/${slug}/catalogue/fournisseurs`)
        }}
        onClose={() => setConfirming(false)}
      />
    </Box>
  )
}
